import { PriceDetails } from '@/types/price';
import { observer } from 'mobx-react-lite';
import { Capacitie } from '@/types/capacitie';
import { useStore } from '@/hooks/useStore';
import { useState, useEffect, useRef, Fragment } from 'react';
import { toJS } from 'mobx';
import { useTranslation } from 'react-i18next';
import ScrollTableGuide from '@/components/general/ScrollTableGuide';
import { DURATION_TIME_TYPES } from '@/configs/constants';
import { formatToCurrency } from '@/utils/formatNumber';

export default observer(function RentalFee() {
    // state
    const [prices, setPrices] = useState<{ prices: PriceDetails | undefined, capacities: Capacitie | undefined }>();

    // store
    const {
        bookingStore: { bikeBookingDetail },
        capacitieStore: { capacitieList },
        priceStore: { priceEUList }
    } = useStore();

    useEffect(() => {
        if (capacitieList && capacitieList.length > 0 && priceEUList && priceEUList.length > 0 && (bikeBookingDetail?.bikeCapacity)) {
            const tmpPriceByBike = toJS(priceEUList).find(item => item?.id === bikeBookingDetail?.location?.priceId);
            const tmpCapacity = toJS(capacitieList).find(item => item?.id === bikeBookingDetail?.bikeCapacity?.id);
            setPrices({ prices: tmpPriceByBike, capacities: tmpCapacity });
        }
    }, [capacitieList, priceEUList, bikeBookingDetail]);

    return (

        <>
            <div className='hidden md:block'>
                <RentalFeeSection item={prices?.prices} capacities={prices?.capacities} />
            </div>
            <div className='block md:hidden'>
                <RentalFeeSectionMobile item={prices?.prices} capacities={prices?.capacities} />
            </div>
        </>
    );
});

// #region Rental Fee Section Mobile
const RentalFeeSectionMobile: React.FC<{
    item: PriceDetails | undefined,
    capacities: Capacitie | undefined
}> = ({ item, capacities }) => {
    // hook
    const { t } = useTranslation();

    return (
        <div>
            <div className='border border-border-gray2 relative'>
                <div className='flex items-center justify-center bg-primaryWeb xsm:h-[50px] h-[40px]'>
                    <p className='font-bold text-white'>レンタル料金</p>
                </div>

                <div className='max-w-full overflow-auto relative'>
                    <div className='grid sm:grid-cols-5 grid-cols-2 divide-x divide-y divide-border-gray2 min-h-[40px]'>
                        {Object.values(DURATION_TIME_TYPES).map(time => (
                            <Fragment key={time.key}>
                                <div className='sm:col-span-2 col-span-1 bg-[#F8F7F1] common-item flex font-medium items-center justify-end h-full !border-l-0'>
                                    {t(`${time.key}`)}
                                </div>
                                <div className='sm:col-span-3 col-span-1 common-item flex justify-center items-center break-words border-t border-border-gray2'>
                                    {formatToCurrency(item?.priceDetails.find(e => e.capacitieId === capacities?.id && e.time === time.value)?.price ?? '0') + t('currency.currency_unit')}
                                </div>
                            </Fragment>
                        ))}
                    </div>
                </div>
            </div>

            <p className='text-fs-mid12 xsm:text-fs-mid13 xsm:leading-[24px] leading-[20px] mt-[10px] text-center text-black-6'>
                ※20歳以下のお客様につきましては、保険適用の特別対
                <br />
                応につきレンタル料金に2,000円が加算されます
                <br />
                ※価格は全て税込みです
            </p>
        </div>
    );
};

// #region Rental Fee Section
const RentalFeeSection: React.FC<{
    item: PriceDetails | undefined,
    capacities: Capacitie | undefined
}> = ({ item, capacities }) => {
    // hook
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showHint, setShowHint] = useState<boolean>(false);
    const onFirstScroll = useRef<boolean>(true);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const checkScrollable = () => setShowHint(el.scrollWidth > el.clientWidth);
        requestAnimationFrame(checkScrollable);

        const handleScroll = () => {
            if (el.scrollLeft > 0 && onFirstScroll.current) {
                onFirstScroll.current = false;
                setShowHint(false);
            }
        };
        el.addEventListener('scroll', handleScroll);
        return () => {
            el.removeEventListener('scroll', handleScroll);
        };
    }, [item?.priceDetails, capacities]);

    return (
        <div>
            <div className='border border-border-gray2 relative'>
                <div className='flex items-center justify-center bg-primaryWeb min-h-[50px]'>
                    <p className='font-bold text-white'>レンタル料金</p>
                </div>
                <ScrollTableGuide showHint={showHint && onFirstScroll.current} />

                <div className='max-w-full overflow-auto relative' ref={scrollRef}>
                    <div className='flex sm:grid grid-cols-4 bg-[#F8F7F1] divide-x divide-y divide-border-gray2 sm:min-h-[60px] min-h-[40px]'>
                        {Object.values(DURATION_TIME_TYPES).map(time => (
                            <div key={time.key} className='bg-[#F8F7F1] min-w-[140px] common-item flex font-medium items-center justify-center h-full'>
                                {t(`${time.key}`)}
                            </div>
                        ))}
                    </div>

                    <div className='flex sm:grid grid-cols-4 divide-x divide-y divide-border-gray2'>
                        {Object.values(DURATION_TIME_TYPES).map(duration => (
                            <div key={duration.value} className='min-w-[140px] common-item flex justify-center items-center break-words border-t border-border-gray2'>
                                {formatToCurrency(item?.priceDetails.find(e => e.capacitieId === capacities?.id && e.time === duration.value)?.price ?? '0') + t('currency.currency_unit')}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <p className='text-fs-mid12 xsm:text-fs-mid13 xsm:leading-[24px] leading-[20px] mt-[10px] text-center text-black-6'>
                ※20歳以下のお客様につきましては、保険適用の特別対応につきレンタル料金に2,000円が加算されます
                <br />
                ※価格は全て税込みです
            </p>
        </div>
    );
};
